import React, { useEffect, useState } from 'react'
import { ProductInterface } from '@pylot-data/fwrdschema'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import Link from '@corratech/pylot-ui/src/Link'
import s from '@components/MultiBuyProducts/MultiBuyProducts.module.scss'
import cn from 'classnames'
import Image from '@corratech/pylot-image'
import { ReadMore } from '@components/Readmore/ReadMore'
import { useTranslation } from 'next-i18next'
import { getPDPUrl } from '@lib/getPDPUrl'
import VariantSelector from '@components/alfakher/pylot-variant-selector/src/VariantSelector/VariantSelector'
import {
    Option,
    ProductStockStatus
} from '@pylot-data/hooks/product/use-product-ui'
import {
    ConfigurableProduct,
    ConfigurableVariant
} from '@pylot-data/pylotschema'
import { SelectedOptions } from '@corratech/pylot-variant-selector'

type MultiBuyProductCardProps = {
    onClick?: () => void
    product: ProductInterface
    selected: boolean
    isDefault?: boolean
    isMobile: boolean
    isPdpProduct?: boolean
    selectedOptions: SelectedOptions
    options: Option[]
    setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
}

export const MultiBuyProductCard = ({
    product,
    onClick = () => null,
    selected,
    isDefault,
    isPdpProduct,
    selectedOptions,
    options,
    setSelectedOptions
}: MultiBuyProductCardProps) => {
    const { url_key } = product
    const productUrl = getPDPUrl(url_key!)
    const { t } = useTranslation('common')
    const [image, setImage] = useState(product?.thumbnail?.url ?? '')

    useEffect(() => {
        const target = (product as ConfigurableProduct)?.variants?.find(
            (variant) =>
                variant?.attributes?.find(
                    (attr) =>
                        attr!.code === 'ooka_color' &&
                        attr!.label === selectedOptions['ooka_color']
                )
        )
        if (target) {
            setImage(target?.product?.thumbnail?.url ?? '')
        }
    }, [product, selectedOptions])

    return (
        <div className={cn(s['multi-buy-productcard'])}>
            <div
                className={cn(s['product-details'], s['productitem-block'], {
                    pdp_product_div: isPdpProduct
                })}
            >
                <div className={cn(s['product-description'])}>
                    <div className={cn(s['productimage-block'])}>
                        {!isPdpProduct && (
                            <div
                                onClick={() => onClick()}
                                role="button"
                                aria-hidden="true"
                                className={cn(s['checkbox-wrapper'])}
                            >
                                <input
                                    type="checkbox"
                                    name="toggle"
                                    id={`toggle-${product.sku}`}
                                    className="form-checkbox"
                                    checked={selected}
                                />
                            </div>
                        )}
                        <div
                            className={cn(
                                s['productimg-link'],
                                'product-card-image-swipe',
                                'slider-product-img'
                            )}
                        >
                            <div
                                className={cn(
                                    s['productitem-image'],
                                    s['multibuy-col']
                                )}
                            >
                                <Link href={productUrl}>
                                    <Image
                                        src={image}
                                        alt={
                                            product.name
                                                ? product.name
                                                : 'placeholder'
                                        }
                                        width={390}
                                        height={390}
                                        className={s['product-img']}
                                    />
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className={s['product-info-box']}>
                        <div className={s['product-name-price']}>
                            <h2
                                className={cn(s['product-name'])}
                                title={product.name || ''}
                            >
                                <Link href={productUrl}> {product.name}</Link>
                            </h2>
                        </div>
                        {isDefault && (
                            <span className={s['default-item']}>
                                {t('This item')}
                            </span>
                        )}
                        <ReadMore
                            className={s['read-more']}
                            content={product.short_description?.html || ''}
                        />
                        <div className={cn(s['product-price-col'])}>
                            {(product as ConfigurableProduct)?.variants
                                ?.length ? (
                                <div
                                    className={cn('pdp-swatch-wrap', 'pdp-row')}
                                >
                                    <VariantSelector
                                        disableAll={
                                            (product as ConfigurableProduct)
                                                .stock_status ===
                                            ProductStockStatus.OutOfStock
                                        }
                                        variants={
                                            (product as ConfigurableProduct)
                                                .variants as ConfigurableVariant[]
                                        }
                                        options={options}
                                        selectedOptions={selectedOptions}
                                        setSelectedOptions={setSelectedOptions}
                                        displaySwatchOptionLabel
                                    />
                                </div>
                            ) : null}

                            <ProductPrice
                                priceRange={product.price_range}
                                is_bulk_product={!!product.is_bulk_product}
                                showDiscountPrice={!product?.is_bulk_product}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
